import { cn } from "@/lib/utils";
import { BookOpen } from "lucide-react";
import { Badge } from "../../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";

import type { IssueType } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardProps,
  BaseEntityCardTags,
  BaseEntityCardTitle,
} from "../BaseCard";

export { IssueType };

type IssueTypeCardProps = IssueType & BaseEntityCardProps;

// Avatar component for IssueTypeCard
export function IssueTypeCardAvatar({
  className,
  style,
}: Partial<IssueTypeCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <BookOpen className="h-5 w-5 text-muted-foreground" />
    </BaseEntityCardIcon>
  );
}

export function IssueTypeCard({
  platform,
  projectId,
  name,
  description,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: IssueTypeCardProps) {
  const descriptionPreview =
    description && description.length > 100
      ? `${description.substring(0, 100)}...`
      : description;

  // Map issue type to color and icon
  const getIssueTypeColor = (typeName: string) => {
    const lowerType = typeName.toLowerCase();
    if (lowerType.includes("bug")) {
      return "bg-red-100 text-red-800";
    } else if (lowerType.includes("story") || lowerType.includes("feature")) {
      return "bg-spectrum-green-light/30 text-green-800";
    } else if (lowerType.includes("epic")) {
      return "bg-plum/10 text-purple-800";
    } else if (lowerType.includes("task")) {
      return "bg-spectrum-blue-light/30 text-teal";
    } else if (
      lowerType.includes("improvement") ||
      lowerType.includes("enhancement")
    ) {
      return "bg-cyan-100 text-cyan-800";
    }
    return "bg-neutral-100 text-neutral-800";
  };

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <IssueTypeCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>{name}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          {descriptionPreview && (
            <BaseEntityCardDescription className="line-clamp-2 text-foreground">
              {descriptionPreview}
            </BaseEntityCardDescription>
          )}

          <BaseEntityCardTags>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className={cn("text-xs", getIssueTypeColor(name))}
                  >
                    {name}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Type</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="text-xs bg-spectrum-blue-light/30 text-teal"
                  >
                    Project ID: {projectId}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Project Identifier</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </BaseEntityCardTags>
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
